import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { tournamentPush } from "../../lib/api";
import {
  Store, Phone, Globe, Mail, MapPin, Eye, EyeOff, Pencil, X, Check, ArrowRightLeft,
  Loader2, ChevronDown, ChevronUp, Save, Bell,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function ShopEditForm({ shop, onSave, onCancel }) {
  const [form, setForm] = useState({ ...shop });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const field = (label, key, type = "text", placeholder = "") => (
    <div>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {key === "description" ? (
        <textarea
          value={form[key] || ""}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="w-full border rounded px-3 py-2 text-sm mt-1 min-h-[80px] resize-y"
          placeholder={placeholder}
          data-testid={`shop-edit-${key}`}
        />
      ) : (
        <Input
          type={type}
          value={form[key] || ""}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="mt-1"
          placeholder={placeholder}
          data-testid={`shop-edit-${key}`}
        />
      )}
    </div>
  );

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {field("Nom", "name", "text", "Nom de la boutique")}
        {field("Ville", "city", "text", "Paris")}
        {field("Adresse", "address", "text", "Adresse complete")}
        {field("Telephone", "phone", "tel", "01 23 45 67 89")}
        {field("Email", "email", "email", "contact@boutique.fr")}
        {field("Site web", "website", "url", "https://...")}
        {field("URL Logo", "logo_url", "url", "https://...")}
        {field("URL Banniere", "banner_url", "url", "https://...")}
      </div>
      {field("Description", "description", "text", "Description de la boutique...")}
      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel}><X className="w-4 h-4 mr-1" />Annuler</Button>
        <Button size="sm" onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Sauvegarder
        </Button>
      </div>
    </div>
  );
}

function TransferModal({ shop, onClose, onTransfer }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleTransfer = async () => {
    if (!email.trim()) { toast.error("Email requis"); return; }
    if (!window.confirm(`Transferer "${shop.name}" a ${email} ? Cette action est irreversible.`)) return;
    setLoading(true);
    await onTransfer(shop.id, email);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="transfer-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 space-y-4">
        <h3 className="font-bold text-lg">Transferer la boutique</h3>
        <p className="text-sm text-muted-foreground">
          Transferer <strong>{shop.name}</strong> a un nouvel utilisateur.
          Si le compte n'existe pas, il sera cree automatiquement avec un mot de passe temporaire envoye par email.
        </p>
        <Input
          type="email"
          placeholder="Adresse email du nouveau proprietaire"
          value={email}
          onChange={e => setEmail(e.target.value)}
          data-testid="transfer-email-input"
        />
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleTransfer} disabled={loading} className="bg-violet-600 hover:bg-violet-700" data-testid="transfer-confirm-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
            Transferer
          </Button>
        </div>
      </div>
    </div>
  );
}

function PushSettings({ shop }) {
  const [status, setStatus] = useState(shop.push_status || "starter");
  const [free, setFree] = useState(shop.push_admin_free || 0);
  const [saving, setSaving] = useState(false);
  const [quota, setQuota] = useState(null);
  const [history, setHistory] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);

  const loadHistory = async () => {
    setLoadingHist(true);
    try {
      const r = await tournamentPush.history(shop.id);
      setHistory(r.data.pushes || []);
    } catch { setHistory([]); }
    finally { setLoadingHist(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await tournamentPush.adminSet(shop.id, { push_status: status, push_admin_free: Number(free) });
      setQuota(r.data);
      toast.success("Réglages push mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 p-3 space-y-2" data-testid={`push-settings-${shop.id}`}>
      <div className="text-xs font-semibold text-violet-600 flex items-center gap-1"><Bell className="w-3.5 h-3.5" />Push tournoi</div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[11px] text-muted-foreground block mb-1">Statut</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm" data-testid={`push-status-${shop.id}`}>
            <option value="starter">Starter (0 gratuit)</option>
            <option value="pro">Pro (2 gratuits/mois)</option>
            <option value="guilde">Guilde (2 gratuits/mois)</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block mb-1">Push offerts (ce mois)</label>
          <Input type="number" min={0} value={free} onChange={(e) => setFree(e.target.value)} className="h-9 w-28" data-testid={`push-free-${shop.id}`} />
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700 h-9" data-testid={`push-save-${shop.id}`}>Enregistrer</Button>
      </div>
      {quota && (
        <div className="text-[11px] text-muted-foreground" data-testid={`push-quota-${shop.id}`}>
          Ce mois : {quota.used}/{quota.cap} utilisés · {quota.free_remaining} gratuit(s) restant(s) (plafond {quota.cap}/mois, offerts inclus).
        </div>
      )}
      <div className="pt-1">
        <Button size="sm" variant="outline" onClick={loadHistory} disabled={loadingHist} className="h-7 text-xs" data-testid={`push-history-btn-${shop.id}`}>
          {loadingHist ? 'Chargement…' : 'Voir l\'historique des push'}
        </Button>
        {history && (
          <div className="mt-2 rounded-md border overflow-hidden" data-testid={`push-history-${shop.id}`}>
            {history.length === 0 ? (
              <div className="p-2 text-[11px] text-muted-foreground">Aucun push envoyé.</div>
            ) : (
              <table className="w-full text-[11px]">
                <thead className="bg-muted/50"><tr>
                  <th className="text-left p-1.5">Date</th><th className="text-left p-1.5">Tournoi</th>
                  <th className="text-right p-1.5">Touchés</th><th className="text-right p-1.5">Convertis</th><th className="text-center p-1.5">Type</th>
                </tr></thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t">
                      <td className="p-1.5 whitespace-nowrap">{h.created_at ? new Date(h.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td className="p-1.5 truncate max-w-[120px]">{h.tournament_name || '—'}</td>
                      <td className="p-1.5 text-right">{h.recipients}</td>
                      <td className="p-1.5 text-right font-semibold text-green-600">{h.conversions}</td>
                      <td className="p-1.5 text-center">{h.paid ? <span className="text-[#FF6B35]">7€</span> : <span className="text-[#4ECDC4]">Gratuit</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


export default function ShopsPage() {
  const { token } = useAuth();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [transferShop, setTransferShop] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchShops = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/shops`, { headers });
      if (res.ok) setShops(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchShops(); }, []);

  const toggleVisibility = async (shopId) => {
    try {
      const res = await fetch(`${API}/api/admin/shops/${shopId}/visibility`, { method: "PUT", headers });
      if (res.ok) {
        const data = await res.json();
        setShops(prev => prev.map(s => s.id === shopId ? { ...s, visible_in_discover: data.visible_in_discover } : s));
        toast.success(data.visible_in_discover ? "Boutique visible" : "Boutique masquee");
      }
    } catch (e) { toast.error("Erreur"); }
  };

  const saveShop = async (form) => {
    try {
      const res = await fetch(`${API}/api/shops/${form.id}`, {
        method: "PUT", headers, body: JSON.stringify(form),
      });
      if (res.ok) {
        const updated = await res.json();
        setShops(prev => prev.map(s => s.id === form.id ? { ...updated, owner: s.owner } : s));
        setEditingId(null);
        toast.success("Boutique mise a jour");
      }
    } catch (e) { toast.error("Erreur"); }
  };

  const handleTransfer = async (shopId, email) => {
    try {
      const res = await fetch(`${API}/api/admin/shops/${shopId}/transfer`, {
        method: "POST", headers, body: JSON.stringify({ email }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message + (data.account_created ? " (nouveau compte cree)" : ""));
        fetchShops();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur reseau"); }
  };

  const visibleCount = shops.filter(s => s.visible_in_discover).length;

  return (
    <div className="space-y-6" data-testid="shops-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="shops-title">Gestion des Boutiques</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {shops.length} boutiques configurees, {visibleCount} visibles dans Decouvrir
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {shops.map(shop => (
            <Card key={shop.id} className={`transition-all ${!shop.visible_in_discover ? "opacity-60 border-dashed" : ""}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  {/* Shop info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Store className="w-5 h-5 text-violet-600 flex-shrink-0" />
                      <span className="font-semibold">{shop.name}</span>
                      {shop.visible_in_discover ? (
                        <Badge className="bg-green-100 text-green-800 text-xs"><Eye className="w-3 h-3 mr-1" />Visible</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs"><EyeOff className="w-3 h-3 mr-1" />Masquee</Badge>
                      )}
                      <Badge variant="outline" className="text-xs">{shop.city}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />{shop.address}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {shop.phone && <span className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" />{shop.phone}</span>}
                      {shop.website && <a href={shop.website} target="_blank" rel="noreferrer" className="text-xs text-violet-500 hover:underline flex items-center gap-1"><Globe className="w-3 h-3" />{new URL(shop.website).hostname}</a>}
                      {shop.email && <span className="text-xs flex items-center gap-1"><Mail className="w-3 h-3" />{shop.email}</span>}
                    </div>
                    {shop.owner && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Proprietaire : <span className="font-medium">{shop.owner.pseudo || shop.owner.name}</span> ({shop.owner.email})
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Switch
                      checked={shop.visible_in_discover}
                      onCheckedChange={() => toggleVisibility(shop.id)}
                      data-testid={`toggle-visibility-${shop.id}`}
                    />
                    <Button variant="ghost" size="sm" onClick={() => setExpandedId(expandedId === shop.id ? null : shop.id)} data-testid={`expand-shop-${shop.id}`}>
                      {expandedId === shop.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {/* Expanded section */}
                {expandedId === shop.id && (
                  <div className="mt-4 space-y-3 border-t pt-4">
                    {shop.description && <p className="text-sm text-muted-foreground">{shop.description}</p>}

                    <PushSettings shop={shop} />
                    
                    {editingId === shop.id ? (
                      <ShopEditForm shop={shop} onSave={saveShop} onCancel={() => setEditingId(null)} />
                    ) : (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditingId(shop.id)} data-testid={`edit-shop-${shop.id}`}>
                          <Pencil className="w-4 h-4 mr-1" />Modifier
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setTransferShop(shop)} data-testid={`transfer-shop-${shop.id}`}>
                          <ArrowRightLeft className="w-4 h-4 mr-1" />Transferer
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {transferShop && (
        <TransferModal
          shop={transferShop}
          onClose={() => setTransferShop(null)}
          onTransfer={handleTransfer}
        />
      )}
    </div>
  );
}
